import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/index/report',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'Redirect',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'RedirectChild',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/register',
    component: () => import('@/views/Login/Register.vue'),
    name: 'Register',
    meta: { hidden: true, title: '注册', noTagsView: true }
  },
  {
    path: '/forgot-password',
    component: () => import('@/views/Login/ForgotPassword.vue'),
    name: 'ForgotPassword',
    meta: { hidden: true, title: '忘记密码', noTagsView: true }
  },
  {
    path: '/trial-expired',
    component: () => import('@/views/Login/TrialExpired.vue'),
    name: 'TrialExpired',
    meta: { hidden: true, title: '会员到期', noTagsView: true }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: t('router.personal'),
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: t('router.personalCenter'),
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/index',
    component: Layout,
    redirect: '/index/report',
    name: 'Index',
    meta: {
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'report',
        component: () => import('@/views/Report/ReportIndex.vue'),
        name: 'ReportIndex',
        meta: {
          title: '首页',
          noCache: false,
          affix: true,
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/sale',
    component: Layout,
    name: 'Sale',
    meta: {
      title: '销售',
      icon: 'ep:shopping-bag',
      alwaysShow: true
    },
    children: [
      {
        path: 'MenuTopSale',
        name: 'MenuTopSale',
        meta: {
          title: '销售'
        }
      },
      {
        path: 'reserve',
        component: () => import('@/views/Sale/SaleReserveList.vue'),
        name: 'SaleReserveList',
        meta: {
          title: '销售预定单',
          belong: 'MenuTopSale',
          permissionKey: 'sale_reserve.read',
          addName: 'SaleReserveEdit'
        }
      },
      {
        path: 'reserve/:id',
        component: () => import('@/views/Sale/SaleReserveEdit.vue'),
        name: 'SaleReserveEdit',
        meta: {
          title: '新增销售预定',
          belong: 'MenuTopSale',
          permissionKey: 'sale_reserve.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'accept',
        component: () => import('@/views/Sale/SaleAcceptList.vue'),
        name: 'SaleAcceptList',
        meta: {
          title: '销售单',
          belong: 'MenuTopSale',
          permissionKey: 'sale_accept.read',
          addName: 'SaleAcceptEdit'
        }
      },
      {
        path: 'accept/:id/:source(\\d+)?',
        component: () => import('@/views/Sale/SaleAcceptEdit.vue'),
        name: 'SaleAcceptEdit',
        meta: {
          title: '新增销售单',
          belong: 'MenuTopSale',
          class: 'menu-item-orange',
          permissionKey: 'sale_accept.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'return',
        component: () => import('@/views/Sale/SaleReturnList.vue'),
        name: 'SaleReturnList',
        meta: {
          title: '销售退货单',
          belong: 'MenuTopSale',
          permissionKey: 'sale_return.read',
          addName: 'SaleReturnEdit'
        }
      },
      {
        path: 'return/:id',
        component: () => import('@/views/Sale/SaleReturnEdit.vue'),
        name: 'SaleReturnEdit',
        meta: {
          title: '新增销售退货',
          belong: 'MenuTopSale',
          permissionKey: 'sale_return.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'retail/:id',
        component: () => import('@/views/Purchase/PurchaseReturnEdit.vue'),
        name: 'SaleRetailEdit',
        meta: {
          title: '零售收银',
          belong: 'MenuTopSale',
          report: 'retail',
          hidden: true
        }
      },
      {
        path: 'retail',
        component: () => import('@/views/Purchase/PurchaseReturnList.vue'),
        name: 'SaleRetailList',
        meta: {
          title: '零售报表',
          hidden: true
        }
      },
      {
        path: 'retail_return/:id',
        component: () => import('@/views/Purchase/PurchaseReturnEdit.vue'),
        name: 'SaleRetailReturnEdit',
        meta: {
          title: '零售退货',
          belong: 'MenuTopSale',
          report: 'retail_return',
          hidden: true
        }
      },
      {
        path: 'retail_return',
        component: () => import('@/views/Purchase/PurchaseReturnList.vue'),
        name: 'SaleRetailReturnList',
        meta: {
          title: '零售退货报表',
          hidden: true
        }
      }
    ]
  },
  {
    path: '/purchase',
    component: Layout,
    name: 'Purchase',
    meta: {
      title: '进货',
      icon: 'ep:shopping-cart',
      alwaysShow: true
    },
    children: [
      {
        path: 'MenuTopPurchase',
        name: 'MenuTopPurchase',
        meta: {
          title: '进货'
        }
      },
      {
        path: 'reserve',
        component: () => import('@/views/Purchase/PurchaseReserveList.vue'),
        name: 'PurchaseReserveList',
        meta: {
          title: '进货预定单',
          belong: 'MenuTopPurchase',
          permissionKey: 'purchase_reserve.read',
          addName: 'PurchaseReserveEdit'
        }
      },
      {
        path: 'reserve/:id',
        component: () => import('@/views/Purchase/PurchaseReserveEdit.vue'),
        name: 'PurchaseReserveEdit',
        meta: {
          title: '进货预定',
          belong: 'MenuTopPurchase',
          permissionKey: 'purchase_reserve.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'accept',
        component: () => import('@/views/Purchase/PurchaseAcceptList.vue'),
        name: 'PurchaseAcceptList',
        meta: {
          title: '进货单',
          belong: 'MenuTopPurchase',
          permissionKey: 'purchase_accept.read',
          addName: 'PurchaseAcceptEdit'
        }
      },
      {
        path: 'accept/:id/:source(\\d+)?',
        component: () => import('@/views/Purchase/PurchaseAcceptEdit.vue'),
        name: 'PurchaseAcceptEdit',
        meta: {
          title: '新增进货单',
          belong: 'MenuTopPurchase',
          permissionKey: 'purchase_accept.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'return',
        component: () => import('@/views/Purchase/PurchaseReturnList.vue'),
        name: 'PurchaseReturnList',
        meta: {
          title: '进货退货单',
          belong: 'MenuTopPurchase',
          permissionKey: 'purchase_return.read',
          addName: 'PurchaseReturnEdit'
        }
      },
      {
        path: 'return/:id',
        component: () => import('@/views/Purchase/PurchaseReturnEdit.vue'),
        name: 'PurchaseReturnEdit',
        meta: {
          title: '新增进货退货',
          belong: 'MenuTopPurchase',
          permissionKey: 'purchase_return.add',
          hidden: false,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/inventory',
    component: Layout,
    name: 'Inventory',
    meta: {
      title: '库存',
      icon: 'ep:copy-document',
      alwaysShow: true
    },
    children: [
      {
        path: 'MenuTopInventory',
        name: 'MenuTopInventory',
        meta: {
          title: '库存'
        }
      },
      {
        path: 'MenuTopInventoryMore',
        name: 'MenuTopInventoryMore',
        meta: {
          title: '更多'
        }
      },
      {
        path: 'list',
        component: () => import('@/views/Inventory/InventoryStockList.vue'),
        name: 'InventoryStockList',
        meta: {
          title: '库存查询',
          belong: 'MenuTopInventory',
          permissionKey: 'inventory.read'
        }
      },
      {
        path: 'transfer',
        component: () => import('@/views/Inventory/InventoryTransferList.vue'),
        name: 'InventoryTransferList',
        meta: {
          title: '调拨单',
          belong: 'MenuTopInventory',
          permissionKey: 'inventory_transfer.read',
          addName: 'InventoryTransferEdit'
        }
      },
      {
        path: 'transfer/:id',
        component: () => import('@/views/Inventory/InventoryTransferEdit.vue'),
        name: 'InventoryTransferEdit',
        meta: {
          title: '新增调拨单',
          belong: 'MenuTopInventory',
          permissionKey: 'inventory_transfer.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'check',
        component: () => import('@/views/Inventory/InventoryCheckList.vue'),
        name: 'InventoryCheckList',
        meta: {
          title: '盘点单',
          belong: 'MenuTopInventory',
          permissionKey: 'inventory_check.read',
          addName: 'InventoryCheckEdit',
          hidden: false
        }
      },
      {
        path: 'check/:id',
        component: () => import('@/views/Inventory/InventoryCheckEdit.vue'),
        name: 'InventoryCheckEdit',
        meta: {
          title: '库存盘点',
          belong: 'MenuTopInventory',
          permissionKey: 'inventory_check.add',
          hidden: true, // 下个版本再开放
          canTo: true
        }
      },
      {
        path: 'other_in',
        component: () => import('@/views/Inventory/InventoryOtherInList.vue'),
        name: 'InventoryOtherInList',
        meta: {
          title: '其他入库单',
          belong: 'MenuTopInventory',
          permissionKey: 'inventory_other_in.read',
          addName: 'InventoryOtherInEdit'
        }
      },
      {
        path: 'other_in/:id',
        component: () => import('@/views/Inventory/InventoryOtherInEdit.vue'),
        name: 'InventoryOtherInEdit',
        meta: {
          title: '新增其他入库单',
          belong: 'MenuTopInventory',
          permissionKey: 'inventory_other_in.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'other_out',
        component: () => import('@/views/Inventory/InventoryOtherOutList.vue'),
        name: 'InventoryOtherOutList',
        meta: {
          title: '其他出库单',
          belong: 'MenuTopInventory',
          permissionKey: 'inventory_other_out.read',
          addName: 'InventoryOtherOutEdit'
        }
      },
      {
        path: 'other_out/:id',
        component: () => import('@/views/Inventory/InventoryOtherOutEdit.vue'),
        name: 'InventoryOtherOutEdit',
        meta: {
          title: '新增其他出库单',
          belong: 'MenuTopInventory',
          permissionKey: 'inventory_other_out.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'warning',
        component: () => import('@/views/Inventory/InventoryWarningList.vue'),
        name: 'InventoryWarningList',
        meta: {
          title: '库存预警',
          belong: 'MenuTopInventoryMore',
          permissionKey: 'inventory_warning.read'
        }
      },
      {
        path: 'batch',
        component: () => import('@/views/Inventory/InventoryBatchList.vue'),
        name: 'InventoryBatchList',
        meta: {
          title: '批次查询',
          belong: 'MenuTopInventoryMore',
          permissionKey: 'inventory_batch.read'
        }
      },
      {
        path: 'expire',
        component: () => import('@/views/Inventory/InventoryExpireList.vue'),
        name: 'InventoryExpireList',
        meta: {
          title: '保质期查询',
          belong: 'MenuTopInventoryMore',
          permissionKey: 'inventory_expire.read'
        }
      }
    ]
  },
  {
    path: '/finance',
    component: Layout,
    name: 'Finance',
    meta: {
      title: '财务',
      icon: 'ep:coin',
      alwaysShow: true
    },
    children: [
      {
        path: 'MenuTopFinance',
        name: 'MenuTopFinance',
        meta: {
          title: '往来账'
        }
      },
      {
        path: 'MenuTopFinanceMore',
        name: 'MenuTopFinanceMore',
        meta: {
          title: '更多财务'
        }
      },
      {
        path: 'payable',
        component: () => import('@/views/Finance/FinancePayableList.vue'),
        name: 'FinancePayableList',
        meta: {
          title: '应付欠款',
          belong: 'MenuTopFinance',
          permissionKey: 'finance_payable.read'
        }
      },
      {
        path: 'receivable',
        component: () => import('@/views/Finance/FinanceReceivableList.vue'),
        name: 'FinanceReceivableList',
        meta: {
          title: '应收欠款',
          belong: 'MenuTopFinance',
          permissionKey: 'finance_receivable.read'
        }
      },
      {
        path: 'pay',
        component: () => import('@/views/Finance/FinancePayList.vue'),
        name: 'FinancePayList',
        meta: {
          title: '付款单',
          belong: 'MenuTopFinance',
          permissionKey: 'finance_pay.read',
          addName: 'FinancePayEdit'
        }
      },
      {
        path: 'pay/:id',
        component: () => import('@/views/Finance/FinancePayEdit.vue'),
        name: 'FinancePayEdit',
        meta: {
          title: '付款单',
          belong: 'MenuTopFinance',
          permissionKey: 'finance_pay.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'receive',
        component: () => import('@/views/Finance/FinanceReceiveList.vue'),
        name: 'FinanceReceiveList',
        meta: {
          title: '收款单',
          belong: 'MenuTopFinance',
          permissionKey: 'finance_receive.read',
          addName: 'FinanceReceiveEdit'
        }
      },
      {
        path: 'receive/:id',
        component: () => import('@/views/Finance/FinanceReceiveEdit.vue'),
        name: 'FinanceReceiveEdit',
        meta: {
          title: '收款单',
          belong: 'MenuTopFinance',
          permissionKey: 'finance_receive.add',
          hidden: false,
          canTo: true
        }
      },
      {
        path: 'write_off',
        component: () => import('@/views/Finance/FinanceWriteOffList.vue'),
        name: 'FinanceWriteOffList',
        meta: {
          title: '核销单',
          belong: 'MenuTopFinance',
          permissionKey: 'finance_write_off.read',
          addName: 'FinanceWriteOffEdit'
        }
      },
      {
        path: 'write_off/:id',
        component: () => import('@/views/Finance/FinanceWriteOffEdit.vue'),
        name: 'FinanceWriteOffEdit',
        meta: {
          title: '核销单',
          belong: 'MenuTopFinance',
          permissionKey: 'finance_write_off.add',
          hidden: false,
          canTo: true,
          class: 'border-bottom'
        }
      },
      {
        path: 'log',
        component: () => import('@/views/Finance/FinanceLogList.vue'),
        name: 'FinanceLogList',
        meta: {
          title: '资金流水',
          belong: 'MenuTopFinance',
          permissionKey: 'finance_log.read'
        }
      },
      {
        path: 'other_income',
        component: () => import('@/views/Finance/FinanceOtherIncomeList.vue'),
        name: 'FinanceOtherIncomeList',
        meta: {
          title: '其他收入',
          belong: 'MenuTopFinanceMore',
          permissionKey: 'finance_other_income.read'
        }
      },
      {
        path: 'other_expense',
        component: () => import('@/views/Finance/FinanceOtherExpenseList.vue'),
        name: 'FinanceOtherExpenseList',
        meta: {
          title: '其他支出',
          belong: 'MenuTopFinanceMore',
          permissionKey: 'finance_other_expense.read'
        }
      },
      // {
      //   path: 'receipt_pay',
      //   component: () => import('@/views/Purchase/Return.vue'),
      //   name: 'ReceiptPay',
      //   meta: {
      //     title: '单据支出',
      //     belong: 'MenuTopFinanceMore'
      //   }
      // },
      {
        path: 'sale_bill',
        component: () => import('@/views/Finance/FinanceSaleBillList.vue'),
        name: 'FinanceSaleBillList',
        meta: {
          title: '销售开票',
          belong: 'MenuTopFinanceMore',
          permissionKey: 'finance_sale_bill.read'
        }
      },
      {
        path: 'purchase_bill',
        component: () => import('@/views/Finance/FinancePurchaseBillList.vue'),
        name: 'FinancePurchaseBillList',
        meta: {
          title: '进货开票',
          belong: 'MenuTopFinanceMore',
          permissionKey: 'finance_purchase_bill.read'
        }
      },
      {
        path: 'transfer',
        component: () => import('@/views/Finance/FinanceTransferList.vue'),
        name: 'FinanceTransferList',
        meta: {
          title: '资金调拨单',
          belong: 'MenuTopFinanceMore',
          permissionKey: 'finance_transfer.read',
          addName: 'FinanceTransferEdit'
        }
      },
      {
        path: 'transfer/:id',
        component: () => import('@/views/Finance/FinanceTransferEdit.vue'),
        name: 'FinanceTransferEdit',
        meta: {
          title: '资金调拨单',
          belong: 'MenuTopFinanceMore',
          permissionKey: 'finance_transfer.add',
          hidden: false,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/report',
    component: Layout,
    name: 'Report',
    meta: {
      title: '报表',
      icon: 'ep:tickets',
      alwaysShow: true
    },
    children: [
      {
        path: 'MenuTopReport',
        name: 'MenuTopReport',
        meta: {
          title: '数据报表'
        }
      },
      {
        path: 'MenuTopReportMore',
        name: 'MenuTopReportMore',
        meta: {
          title: '更多'
        }
      },
      {
        path: 'sale/doc',
        component: () => import('@/views/Report/ReportSaleDocList.vue'),
        name: 'ReportSaleDocList',
        meta: {
          title: '销售利润表(按单据)',
          belong: 'MenuTopReport',
          permissionKey: 'report_sale_doc.read'
        }
      },
      {
        path: 'sale/product',
        component: () => import('@/views/Report/ReportSaleProductList.vue'),
        name: 'ReportSaleProductList',
        meta: {
          title: '销售利润表(按商品)',
          belong: 'MenuTopReport',
          permissionKey: 'report_sale_product.read'
        }
      },
      {
        path: 'sale/customer',
        component: () => import('@/views/Report/ReportSalePartnerList.vue'),
        name: 'ReportSalePartnerList',
        meta: {
          title: '销售利润表(按客户)',
          belong: 'MenuTopReport',
          permissionKey: 'report_sale_customer.read',
          class: 'border-bottom'
        }
      },
      {
        path: 'sale_analysis',
        component: () => import('@/views/Purchase/PurchaseReturnList.vue'),
        name: 'ReportSaleAnalysisList',
        meta: {
          title: '销售分析',
          hidden: true,
          belong: 'MenuTopReport',
          permissionKey: 'report_sale_product.read'
        }
      },
      {
        path: 'purchase/doc',
        component: () => import('@/views/Report/ReportPurchaseDocList.vue'),
        name: 'ReportPurchaseDocList',
        meta: {
          title: '进货统计(按单据)',
          belong: 'MenuTopReport',
          permissionKey: 'report_purchase_doc.read'
        }
      },
      {
        path: 'purchase/product',
        component: () => import('@/views/Report/ReportPurchaseProductList.vue'),
        name: 'ReportPurchaseProductList',
        meta: {
          title: '进货统计(按商品)',
          belong: 'MenuTopReport',
          permissionKey: 'report_purchase_product.read'
        }
      },
      {
        path: 'purchase_sale_analysis',
        component: () => import('@/views/Purchase/PurchaseReturnList.vue'),
        name: 'ReportPurchaseSaleAnalysisList',
        meta: {
          title: '进销对比分析',
          hidden: true,
          belong: 'MenuTopReport',
          permissionKey: 'report_purchase_product.read'
        }
      },
      {
        path: 'sale/staff',
        component: () => import('@/views/Report/ReportSaleStaffList.vue'),
        name: 'ReportSaleStaffList',
        meta: {
          title: '员工销售业绩',
          belong: 'MenuTopReportMore',
          permissionKey: 'report_staff.read'
        }
      },
      {
        path: 'sale/salesman',
        component: () => import('@/views/Report/ReportSaleSalesmanList.vue'),
        name: 'ReportSaleSalesmanList',
        meta: {
          title: '业务员销售业绩',
          belong: 'MenuTopReportMore',
          permissionKey: 'report_salesman.read'
        }
      },
      {
        path: 'product/top',
        component: () => import('@/views/Report/ReportProductTopList.vue'),
        name: 'ReportProductTopList',
        meta: {
          title: '商品销售排行',
          belong: 'MenuTopReportMore',
          permissionKey: 'report_top.read',
          class: 'border-bottom'
        }
      },
      {
        path: 'profit',
        component: () => import('@/views/Report/ReportProfitList.vue'),
        name: 'ReportProfitList',
        meta: {
          title: '经营利润',
          belong: 'MenuTopReportMore',
          permissionKey: 'report_profit.read'
        }
      },
      {
        path: 'sn_trace',
        component: () => import('@/views/Report/ReportSnTraceList.vue'),
        name: 'ReportSnTraceList',
        meta: {
          title: '串码跟踪表',
          belong: 'MenuTopReportMore',
          permissionKey: 'report_serial.read'
        }
      },
      {
        // 视频教程：入口在顶部工具栏（VideoGuideEntryButton），不进报表菜单避免重复。
        // hidden 必须配 canTo：generateRoutesByFrontEnd 对 `hidden && !canTo` 是直接
        // continue 丢弃路由（不只是菜单不显示），漏了 canTo 会 "No match for name"。
        // 帮助内容不设权限，横屏教程由服务端按 X-Erp-Platform 下发。
        path: 'video',
        component: () => import('@/views/Report/video/VideoList.vue'),
        name: 'ReportVideoList',
        meta: {
          title: '视频教程',
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/material',
    component: Layout,
    name: 'Material',
    meta: {
      title: '资料',
      icon: 'ep:set-up',
      alwaysShow: true
    },
    children: [
      {
        path: 'MenuTopProductBase',
        name: 'MenuTopProductBase',
        meta: {
          title: '基本资料'
        }
      },
      {
        path: 'MenuTopProductUnit',
        name: 'MenuTopProductUnit',
        meta: {
          title: '辅助资料'
        }
      },
      {
        path: 'product',
        component: () => import('@/views/Material/MaterialProductList.vue'),
        name: 'MaterialProductList',
        meta: {
          title: '商品管理',
          belong: 'MenuTopProductBase',
          class: 'menu-item-orange',
          permissionKey: 'base_material.read'
        }
      },
      {
        path: 'product/:id',
        component: () => import('@/views/Material/MaterialProductEdit.vue'),
        name: 'MaterialProductEdit',
        meta: {
          title: '新增商品',
          hidden: true,
          canTo: true,
          permissionKey: 'base_material.add'
        }
      },
      {
        path: 'customer',
        component: () => import('@/views/Material/MaterialCustomerList.vue'),
        name: 'MaterialCustomerList',
        meta: {
          title: '客户',
          belong: 'MenuTopProductBase',
          permissionKey: 'base_material.read'
        }
      },
      {
        path: 'supplier',
        component: () => import('@/views/Material/MaterialSupplierList.vue'),
        name: 'MaterialSupplierList',
        meta: {
          title: '供应商',
          belong: 'MenuTopProductBase',
          permissionKey: 'base_material.read'
        }
      },
      {
        path: 'warehouse',
        component: () => import('@/views/Material/MaterialWarehouseList.vue'),
        name: 'MaterialWarehouseList',
        meta: {
          title: '仓库',
          belong: 'MenuTopProductBase',
          permissionKey: 'base_material.read'
        }
      },
      {
        path: 'account',
        component: () => import('@/views/Material/MaterialAccountList.vue'),
        name: 'MaterialAccountList',
        meta: {
          title: '结算账户',
          belong: 'MenuTopProductBase',
          permissionKey: 'base_material.read'
        }
      },
      {
        path: 'store',
        component: () => import('@/views/Material/MaterialStoreList.vue'),
        name: 'MaterialStoreList',
        meta: {
          title: '门店',
          belong: 'MenuTopProductBase',
          permissionKey: 'base_material.read'
        }
      },
      {
        path: 'salesman',
        component: () => import('@/views/System/SystemSalesmanList.vue'),
        name: 'SystemSalesmanList',
        meta: {
          title: '业务员',
          belong: 'MenuTopProductBase',
          permissionKey: 'salesman_mange.read'
        }
      },
      {
        path: 'class',
        component: () => import('@/views/Material/MaterialProductClassList.vue'),
        name: 'MaterialProductClassList',
        meta: {
          title: '商品分类',
          belong: 'MenuTopProductUnit',
          permissionKey: 'other_material.read'
        }
      },
      {
        path: 'sz_class',
        component: () => import('@/views/Material/MaterialSzClassList.vue'),
        name: 'MaterialSzClassList',
        meta: {
          title: '收支分类',
          belong: 'MenuTopProductUnit',
          permissionKey: 'other_material.read'
        }
      },
      {
        path: 'attribute',
        component: () => import('@/views/Material/MaterialAttributeList.vue'),
        name: 'MaterialAttributeList',
        meta: {
          title: '属性设置',
          belong: 'MenuTopProductUnit',
          permissionKey: 'other_material.read'
        }
      },
      {
        path: 'unit',
        component: () => import('@/views/Material/MaterialUnitList.vue'),
        name: 'MaterialUnitList',
        meta: {
          title: '计量单位',
          belong: 'MenuTopProductUnit',
          permissionKey: 'other_material.read'
        }
      },
      {
        path: 'brand',
        component: () => import('@/views/Material/MaterialBrandList.vue'),
        name: 'MaterialBrandList',
        meta: {
          title: '商品品牌',
          belong: 'MenuTopProductUnit',
          permissionKey: 'other_material.read'
        }
      },
      {
        path: 'commission',
        component: () => import('@/views/Material/MaterialCommissionList.vue'),
        name: 'MaterialCommissionList',
        meta: {
          title: '提成规则',
          belong: 'MenuTopProductUnit',
          permissionKey: 'other_material.read'
        }
      },
      {
        path: 'grade_price',
        component: () => import('@/views/Material/MaterialGradePriceList.vue'),
        name: 'MaterialGradePriceList',
        meta: {
          title: '等级管理',
          belong: 'MenuTopProductUnit',
          permissionKey: 'other_material.read'
        }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    name: 'System',
    meta: {
      title: '设置',
      icon: 'ep:setting',
      alwaysShow: true
    },
    children: [
      {
        path: 'MenuTopSystem',
        name: 'MenuTopSystem',
        meta: {
          title: '高级设置'
        }
      },
      {
        path: 'config',
        component: () => import('@/views/System/Config.vue'),
        name: 'SystemConfig',
        meta: {
          title: '系统参数',
          belong: 'MenuTopSystem',
          permissionKey: 'system_config.read'
        }
      },
      {
        path: 'staff',
        component: () => import('@/views/System/SystemStaffList.vue'),
        name: 'SystemStaffList',
        meta: {
          title: '职员管理',
          belong: 'MenuTopSystem',
          permissionKey: 'staff_manage.read'
        }
      },
      {
        // 角色管理：与职员管理同级，权限沿用 staff_manage（管理角色即管理员工权限）
        path: 'role',
        component: () => import('@/views/System/SystemRoleList.vue'),
        name: 'SystemRoleList',
        meta: {
          title: '角色管理',
          belong: 'MenuTopSystem',
          permissionKey: 'staff_manage.read'
        }
      },
      {
        path: 'printer',
        component: () => import('@/views/System/SystemPrinterTemplateList.vue'),
        name: 'SystemPrinterTemplateList',
        meta: {
          title: '打印模板',
          belong: 'MenuTopSystem',
          permissionKey: 'print_template.read'
        }
      },
      {
        path: 'label',
        component: () => import('@/views/System/SystemLabelTemplateList.vue'),
        name: 'SystemLabelTemplateList',
        meta: {
          title: '标签模板',
          belong: 'MenuTopSystem',
          permissionKey: 'print_template.read'
        }
      },
      // {
      //   path: 'rule',
      //   component: () => import('@/views/System/NoRule.vue'),
      //   name: 'NoRule',
      //   meta: {
      //     title: '单号规则',
      //     belong: 'MenuTopSystem'
      //   }
      // },
      {
        path: 'backup',
        component: () => import('@/views/System/SystemBackupList.vue'),
        name: 'SystemBackupList',
        meta: {
          title: '数据备份',
          belong: 'MenuTopSystem',
          permissionKey: 'backup.read'
        }
      },
      {
        path: 'log',
        component: () => import('@/views/System/SystemLogList.vue'),
        name: 'SystemLogList',
        meta: {
          title: '操作日志',
          belong: 'MenuTopSystem',
          permissionKey: 'system_log.read'
        }
      }
    ]
  },
  // 租户管理：不挂设置菜单、不套公共 Layout，仅手动输入 /#/system/user 进入
  {
    path: '/system/user',
    component: () => import('@/views/System/SystemUserList.vue'),
    name: 'SystemUserList',
    meta: {
      title: '租户管理',
      permissionKey: 'admin.read',
      hidden: true,
      canTo: true,
      noTagsView: true
    }
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
